import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, Badge, Button } from '@/shared/ui';
import { SectionHeader } from '../components/SectionHeader';
import { StatusPills } from '../components/StatusPills';
import { QuestionInput } from './QuestionInput';
import { brandQuestionsFor } from '../constants/brandQuestions';
import { useVertical } from '@/shared/hooks/useVertical';
import type { Pick as BrandPick } from '../constants/brandQuestions';
import type { StrategyQuestion } from '../api/strategyApi';
import styles from './PracticeProfileStep.module.css';

interface Props {
  yourBrandQuestions: StrategyQuestion[];
  picks: BrandPick[];
  togglePick: (i: number, j: 0 | 1) => void;
  responses: Record<string, unknown>;
  setResponse: (questionId: string, value: unknown) => void;
  confirmedSections: Set<string>;
  brandSectionDone: boolean;
  brandFillStatus: 'filled' | 'partially-filled' | null;
  openSubSection: string | null;
  setOpenSubSection: Dispatch<SetStateAction<string | null>>;
  section1Done: boolean;
  setSection1Expanded: Dispatch<SetStateAction<boolean>>;
  subSectionHeaderRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  pendingScrollToKey: MutableRefObject<string | null>;
  confirmBrandSection: () => void;
}

export function BrandPositioningSection({
  yourBrandQuestions,
  picks,
  togglePick,
  responses,
  setResponse,
  brandSectionDone,
  brandFillStatus,
  openSubSection,
  setOpenSubSection,
  section1Done,
  setSection1Expanded,
  subSectionHeaderRefs,
  pendingScrollToKey,
  confirmBrandSection,
}: Props) {
  // Question wording is vertical-specific; keys and order are not.
  const { verticalId } = useVertical();
  const brandQuestions = brandQuestionsFor(verticalId);
  const missingPicks = picks.filter((p) => p === null).length;
  const missingRequired = yourBrandQuestions.filter((q) => {
    const v = responses[q.id];
    return (
      q.required &&
      (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0))
    );
  }).length;
  const missingCount = missingPicks + missingRequired;

  return (
    <>
      <SectionHeader
        n={3}
        done={brandSectionDone}
        title="Your Brand & Positioning"
        estimate="~3 min"
      />
      <p className={styles.sectionSubtitle}>
        Tell us how you practice and what drives you. We'll use this to anchor your strategy.
      </p>

      <div className={styles.sectionList}>
        <Card padding="none" className={styles.accordionCard}>
          <div
            ref={(el) => {
              subSectionHeaderRefs.current['brand'] = el;
            }}
            onClick={() => {
              const isOpening = openSubSection !== 'brand';
              if (isOpening) {
                pendingScrollToKey.current = 'brand';
                if (section1Done) setSection1Expanded(false);
              }
              setOpenSubSection((p) => (p === 'brand' ? null : 'brand'));
            }}
            className={styles.accordionHeader}
          >
            {brandSectionDone && (
              <span className={styles.accentIcon}>
                <Check size={14} strokeWidth={2.5} />
              </span>
            )}
            <span className={styles.accordionHeaderTitle}>Brand Questionnaire</span>
            <StatusPills fill={brandFillStatus} source={null} confirmed={brandSectionDone} />
            <span className={styles.accordionChevron}>
              {openSubSection === 'brand' ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </span>
          </div>

          {openSubSection === 'brand' && (
            <div className={styles.accordionBody}>
              {yourBrandQuestions.map((q) => (
                <div key={q.id}>
                  <div
                    className={`${styles.questionLabel} ${q.description ? styles.questionLabelWithDesc : ''}`}
                  >
                    {q.label}
                    {q.required && <span className={styles.requiredAsterisk}>*</span>}
                  </div>
                  {q.description && <div className={styles.questionDesc}>{q.description}</div>}
                  <QuestionInput
                    question={q}
                    value={responses[q.id]}
                    onChange={(v) => setResponse(q.id, v)}
                  />
                </div>
              ))}

              <div>
                <div className={styles.brandList}>
                  {brandQuestions.map((item, i) => (
                    <div key={item.questionKey}>
                      <div className={styles.archetypeHeader}>
                        <span className={styles.archetypeAxis}>
                          {item.axis === 'A' ? 'CARE' : 'DRIVE'}
                        </span>
                        <span className={styles.archetypeQ}>{item.q}</span>
                      </div>
                      <div className={styles.archetypeGrid}>
                        {(item.opts as readonly string[]).map((opt, j) => (
                          <button
                            key={j}
                            onClick={() => togglePick(i, j as 0 | 1)}
                            className={`${styles.archetypeOpt} ${picks[i] === j ? styles.archetypeOptSelected : ''}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.confirmFooter} onClick={(e) => e.stopPropagation()}>
                {brandSectionDone ? (
                  <Badge variant="primary">
                    <span className={styles.badgeContent}>
                      <Check size={11} aria-hidden /> Confirmed
                    </span>
                  </Badge>
                ) : (
                  <>
                    {missingCount > 0 && (
                      <span className={styles.confirmHint}>
                        {missingCount} answer{missingCount === 1 ? '' : 's'} still needed to confirm
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={missingCount > 0}
                      onClick={confirmBrandSection}
                    >
                      Confirm section
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
